/**
 * Detects when the user enters the apply flow from outside it, and flags
 * the page so it resets any leftover in-memory state from a previous visit.
 *
 * `useState` survives SPA navigation, so without this flag a user who opens
 * a draft, navigates back to the subsidy list, and clicks "Apply" again
 * silently resumes their old draft — when their intent is clearly "start a
 * new application." We can't decide this from the URL alone because the
 * apply flow's own internal navigation (back button, sub-step links) routes
 * through `/subsidies/[id]/apply` (no application_id) — same URL, opposite
 * intent. The from-route is the disambiguator.
 *
 * Reloads bypass this naturally: on a hard reload the JS runtime restarts,
 * `useState` is empty, and the apply page's onMounted restores from
 * `sessionStorage` (the autosave recovery key).
 */
/**
 * @param {string} path - Route path to test.
 * @returns {boolean} True if the path is on the standard apply flow.
 */
const isApplyRoute = (path: string) => /^\/subsidies\/\d+\/apply(\/|$)/.test(path)

/**
 * @param {string} path - Route path to test.
 * @returns {boolean} True if the path is on the resume-draft flow.
 */
const isApplyDraftRoute = (path: string) => /^\/subsidies\/apply-draft\/\d+\//.test(path)

/**
 * Global Nuxt route middleware. See file-level comment.
 * @param {object} to - Destination route.
 * @param {object} [from] - Previous route, when available.
 */
export default defineNuxtRouteMiddleware((to, from) => {
  if (!import.meta.client) return
  if (!isApplyRoute(to.path) && !isApplyDraftRoute(to.path)) return

  const cameFromApplyFlow = !!from && (isApplyRoute(from.path) || isApplyDraftRoute(from.path))

  if (!cameFromApplyFlow) {
    useState('apply-needs-reset', () => false).value = true
  }
})
